import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

export default function AdminCoupons() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<any>({});

  const { data: coupons = [], isLoading } = useQuery({
    queryKey: ['coupons'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get('http://localhost:5000/api/coupons', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (coupon: any) => {
      const token = localStorage.getItem('access_token');
      await axios.post('http://localhost:5000/api/coupons', coupon, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      setIsAdding(false);
      setFormData({});
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('access_token');
      await axios.delete(`http://localhost:5000/api/coupons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coupons'] })
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      discountType: Number(formData.discountType) || 0,
      discountValue: Number(formData.discountValue) || 0,
      minOrderAmount: formData.minOrderAmount ? Number(formData.minOrderAmount) : null,
      isActive: true
    });
  };

  if (isLoading) return <div className="p-8"><div className="skeleton h-64 w-full"></div></div>;

  return (
    <div className="p-6 animate-fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Kupon Yönetimi</h1>
          <p className="text-muted mt-2">Müşteriler için indirim kuponları oluşturun.</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn btn-primary">
          <Plus size={20} /> Yeni Kupon
        </button>
      </div>

      {isAdding && (
        <div className="card mb-8">
          <h3 className="font-bold mb-4">Yeni Kupon Oluştur</h3>
          <form onSubmit={handleSave} className="grid-2 gap-4">
            <div className="form-group">
              <label className="form-label">Kupon Kodu</label>
              <input required type="text" className="form-input" placeholder="YAZ2026" 
                value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">İndirim Tipi</label>
              <select className="form-input" value={formData.discountType || 0} onChange={e => setFormData({...formData, discountType: e.target.value})}>
                <option value="0">Yüzde (%)</option>
                <option value="1">Sabit Tutar (₺)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">İndirim Değeri</label>
              <input required type="number" className="form-input" placeholder="20" 
                value={formData.discountValue || ''} onChange={e => setFormData({...formData, discountValue: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Min Sipariş Tutarı (₺)</label>
              <input type="number" className="form-input" placeholder="100" 
                value={formData.minOrderAmount || ''} onChange={e => setFormData({...formData, minOrderAmount: e.target.value})} />
            </div>
            
            <div className="col-span-2 flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setIsAdding(false)} className="btn btn-ghost">İptal</button>
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>Oluştur</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid-3 gap-6">
        {coupons.map((c: any) => (
          <div key={c.id} className="card relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary"></div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-sm text-primary font-bold tracking-wider mb-1">KOD</div>
                <h3 className="text-xl font-black">{c.code}</h3>
              </div>
              <span className={`badge ${c.isActive ? 'badge-success' : 'badge-neutral'}`}>
                {c.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            
            <div className="flex flex-col gap-2 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-muted">İndirim:</span>
                <span className="font-bold">{c.discountType === 0 ? `%${c.discountValue}` : `${c.discountValue} ₺`}</span>
              </div>
              {c.minOrderAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted">Min Sepet:</span>
                  <span>{c.minOrderAmount} ₺</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted">Kullanım:</span>
                <span>{c.usedCount} kez kullanıldı</span>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end pt-4 border-t border-border">
              <button 
                onClick={() => { if(confirm('Silmek istediğinize emin misiniz?')) deleteMutation.mutate(c.id); }} 
                className="btn btn-sm btn-ghost text-error hover:bg-error hover:text-white"
              >
                <Trash2 size={16} /> Sil
              </button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && !isAdding && (
          <div className="col-span-3 p-12 text-center text-muted card border-dashed">
            Henüz kupon oluşturulmamış. Yeni bir kupon ekleyerek başlayın.
          </div>
        )}
      </div>
    </div>
  );
}
